import { Injectable } from '@angular/core';
import { PublicHttpService } from './public-http.service';
import { TenantContextService } from './tenant-context.service';

export type PatientFieldType = 'text' | 'select' | 'textarea';

export type PatientField = {
  id: string;
  label: string;
  placeholder?: string;
  type: PatientFieldType;
  required?: boolean;
  column?: number;
  options?: string[];
};

export type VisitReason = {
  label: string;
  code: string;
  requiresNotes?: boolean;
};

export type VisitAttributesConfig = {
  reasons: VisitReason[];
  clinicTypes: string[];
  paymentModes: string[];
  hmoStatuses: string[];
  triagePriorities: string[];
};

export type VitalsField = {
  id: string;
  label: string;
  placeholder?: string;
  type?: PatientFieldType;
};

export type CorneaRowConfig = {
  key: string;
  label: string;
  options: string[];
  defaultValue: string;
};

type ConfigPayloads = {
  patientAttributes: { fields: PatientField[] };
  visitAttributes: VisitAttributesConfig;
  vitals: { fields: VitalsField[] };
  corneaDetails: { rows: CorneaRowConfig[] };
};

const DEFAULT_CONFIG: ConfigPayloads = {
  patientAttributes: {
    fields: [
      {
        id: 'firstName',
        label: 'First Name',
        placeholder: 'First name',
        type: 'text',
        required: true,
        column: 1,
      },
      {
        id: 'middleName',
        label: 'Middle Name',
        placeholder: 'Middle name (optional)',
        type: 'text',
        column: 1,
      },
      {
        id: 'lastName',
        label: 'Last Name',
        placeholder: 'Last name',
        type: 'text',
        required: true,
        column: 1,
      },
      {
        id: 'contactNumber',
        label: 'Contact Number',
        placeholder: '+63 9xx xxx xxxx',
        type: 'text',
        required: true,
        column: 1,
      },
      {
        id: 'dateOfBirth',
        label: 'Birthdate',
        placeholder: 'MM/DD/YYYY',
        type: 'text',
        column: 1,
      },
      {
        id: 'sex',
        label: 'Sex',
        placeholder: 'Select',
        type: 'select',
        column: 1,
        options: ['Female', 'Male', 'Other'],
      },
      {
        id: 'email',
        label: 'Email',
        placeholder: 'name@email.com',
        type: 'text',
        column: 2,
      },
      {
        id: 'address',
        label: 'Address',
        placeholder: 'Complete address',
        type: 'text',
        column: 2,
      },
      {
        id: 'occupation',
        label: 'Occupation',
        placeholder: 'Occupation',
        type: 'text',
        column: 2,
      },
      {
        id: 'referredBy',
        label: 'Referred By',
        placeholder: 'Referral source',
        type: 'text',
        column: 2,
      },
      {
        id: 'mrn',
        label: 'MRN (optional)',
        placeholder: 'Medical record number',
        type: 'text',
        column: 2,
      },
      {
        id: 'pin',
        label: 'PIN (optional)',
        placeholder: 'Patient PIN',
        type: 'text',
        column: 2,
      },
    ],
  },
  visitAttributes: {
    reasons: [
      { label: 'Refractive Error Consult', code: 'A' },
      { label: 'Medical / Ophtha Consult', code: 'B' },
      { label: 'For Surgery Only', code: 'C' },
      { label: 'For Diagnostics Only', code: 'D' },
      { label: 'Other', code: 'O', requiresNotes: true },
    ],
    clinicTypes: [
      'General Ophtha',
      'Retina',
      'Glaucoma',
      'Uveitis',
      'Neuro Ophtha',
      'Orbit',
      'Pedia Motility',
      'External Disease',
    ],
    paymentModes: ['Cash', 'HMO', 'Company', 'PhilHealth'],
    hmoStatuses: ['Not Started', 'Submitted', 'Approved', 'Denied', 'Pending'],
    triagePriorities: ['Routine', 'Priority', 'Urgent'],
  },
  vitals: {
    fields: [
      { id: 'bloodPressure', label: 'Blood Pressure', placeholder: '120/80', type: 'text' },
      { id: 'heartRate', label: 'Heart Rate', placeholder: 'bpm', type: 'text' },
      { id: 'temperature', label: 'Temperature', placeholder: '°C', type: 'text' },
      { id: 'oxygenSaturation', label: 'O2 Saturation', placeholder: '%', type: 'text' },
      { id: 'notes', label: 'Notes', placeholder: 'Additional notes', type: 'textarea' },
    ],
  },
  corneaDetails: {
    rows: [
      {
        key: 'clarity',
        label: 'Clarity',
        defaultValue: 'Normal',
        options: ['Normal', 'Clear', 'Hazy', 'Opaque', 'Edema', 'Other'],
      },
      {
        key: 'epithelium',
        label: 'Epithelium',
        defaultValue: 'Intact',
        options: ['Intact', 'SPKs', 'Erosion', 'Ulcer', 'Epithelial Defect', 'Other'],
      },
      {
        key: 'stroma',
        label: 'Stroma',
        defaultValue: 'Clear',
        options: ['Clear', 'Infiltrate', 'Scar', 'Dystrophy', 'Other'],
      },
      {
        key: 'descemet',
        label: 'Descemet',
        defaultValue: 'Normal',
        options: ['Normal', 'Folds', 'Tear', 'Other'],
      },
      {
        key: 'endothelium',
        label: 'Endothelium',
        defaultValue: 'Normal',
        options: ['Normal', 'Guttata', 'Beaten-Metal', 'Other'],
      },
      {
        key: 'arcus',
        label: 'Arcus',
        defaultValue: 'None',
        options: ['None', 'Present', 'Other'],
      },
      {
        key: 'keraticPrecipitates',
        label: 'Keratic Precipitates',
        defaultValue: 'None',
        options: ['None', 'Fine', 'Stellate', 'Mutton fat', 'Other'],
      },
      {
        key: 'others',
        label: 'Others',
        defaultValue: 'None',
        options: ['None', 'Specify in notes'],
      },
    ],
  },
};

type ListConfigsResponse = {
  listConfigs: {
    items: Array<{
      configKey: string;
      payload: unknown;
    }>;
  };
};

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly config = new Map<string, unknown>();

  constructor(
    private readonly publicHttp: PublicHttpService,
    private readonly tenantContext: TenantContextService,
  ) {}

  async load(): Promise<void> {
    const query = `
      query ListConfigs($tenantId: ID!, $limit: Int, $onlyActive: Boolean) {
        listConfigs(tenantId: $tenantId, limit: $limit, onlyActive: $onlyActive) {
          items {
            configKey
            payload
          }
        }
      }
    `;

    try {
      const data = await this.publicHttp.query<ListConfigsResponse>(query, {
        tenantId: this.tenantContext.getTenantIdOrFallback(),
        limit: 25,
        onlyActive: true,
      });
      for (const item of data.listConfigs?.items ?? []) {
        const normalized = this.normalizePayload(item.payload);
        this.config.set(item.configKey, normalized);
      }
    } catch (error) {
      console.warn('Unable to load config, using defaults.', error);
    }
  }

  getPatientFields(): PatientField[] {
    return this.getConfig<ConfigPayloads['patientAttributes']>('patientAttributes', DEFAULT_CONFIG.patientAttributes)
      .fields;
  }

  getVisitAttributes(): VisitAttributesConfig {
    return this.getConfig<ConfigPayloads['visitAttributes']>('visitAttributes', DEFAULT_CONFIG.visitAttributes);
  }

  getVisitReasons(): string[] {
    return this.getVisitAttributes().reasons.map((reason) => reason.label);
  }

  requiresReasonNotes(reason?: string): boolean {
    if (!reason) {
      return false;
    }
    return Boolean(this.getVisitAttributes().reasons.find((item) => item.label === reason)?.requiresNotes);
  }

  getReasonCode(reason?: string): string {
    if (!reason) {
      return 'O';
    }
    const match = this.getVisitAttributes().reasons.find((item) => item.label === reason);
    return match?.code ?? 'O';
  }

  getVitalsFields(): VitalsField[] {
    return this.getConfig<ConfigPayloads['vitals']>('vitals', DEFAULT_CONFIG.vitals).fields;
  }

  getCorneaRows(): CorneaRowConfig[] {
    return this.getConfig<ConfigPayloads['corneaDetails']>('corneaDetails', DEFAULT_CONFIG.corneaDetails).rows;
  }

  getCorneaDefaults(): Record<string, string> {
    const defaults: Record<string, string> = {};
    for (const row of this.getCorneaRows()) {
      defaults[row.key] = row.defaultValue;
    }
    return defaults;
  }

  setConfig(key: string, payload: unknown): void {
    if (!key) {
      return;
    }
    this.config.set(key, payload);
  }

  private getConfig<T>(key: string, fallback: T): T {
    const value = this.config.get(key);
    if (value) {
      return value as T;
    }
    return fallback;
  }

  private normalizePayload(payload: unknown): unknown {
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch {
        return payload;
      }
    }
    return payload ?? null;
  }
}
